import axios from 'axios';
import config from '../../config';
import { v4 as uuid } from 'uuid';

class AutoSaveManager {
  constructor(formComponent) {
    this.form = formComponent;
    this.autoSaveInterval = null;
  }

  // Handles the auto-save logic
  handleAutoSave = async (formMeta, session_id) => {
    if (this.form.state.previewMode || this.form.state.canReadOnly) return; // If preview or read only, don't save

    // Check if either can save submission or autosave are false
    // If so, return, else continue
    // v2: formMeta.canSaveSubmission / formMeta.autoSave
    // v1: formMeta.data.data.canSaveSubmission / formMeta.data.data.autoSave
    const canSave = formMeta.canSaveSubmission ?? formMeta.data?.data?.canSaveSubmission;
    const autoSave = formMeta.autoSave ?? formMeta.data?.data?.autoSave;
    if (!canSave || !autoSave)
      return;

    // Need to know if it is a form session or not
    // If there is no session_id, we need to create one and then save
    if (!session_id || session_id.trim() == '') {
      let { form_id, test_id } = this.form.props.match.params;

      let failedUploads = {};
      const data = await this.form.traverseAndFlatten(
        this.form.props.form.answer_data,
        'SAVE',
        null,
        failedUploads
      );

      const saveData = data ? { ...data } : {};

      // Remove any failedUploads
      if (failedUploads && Object.keys(failedUploads).length > 0) {
        Object.keys(failedUploads).map((key) => {
          if (key in saveData) delete saveData[key];
        });
      }

      const isTest = test_id !== undefined;

      // Set url conditionally on whether it's a test or not
      let sessionURL = isTest
        ? `${config.API_ADDRESS}/test_form_session?session_id=${session_id}&form_id=${form_id}`
        : `${config.API_ADDRESS}/form_session?session_id=${session_id}&form_id=${form_id}`;

      session_id = uuid(); // Generate a session id

      axios(sessionURL, {
        method: 'post',
        data: {
          data: saveData,
          session_id: session_id,
          form_id: this.form.props.match.params.form_id,
          password: this.form.state.password,
          projectSessionId: this.form.state.projectSessionId,
          projectVersion: this.form.state.projectVersion,
          isSingleUse: this.form.state.isSingleUse,
        },
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      })
        .then(() => {
          this.form.setState({ pageProcessing: false });
          let formSessionUrl = `/form/${this.form.props.match.params.form_id}/${session_id}`;
          if (isTest)
            formSessionUrl = `/form/form_test/${this.form.props.match.params.form_id}/${test_id}/${session_id}`;
          this.form.props.history.push(formSessionUrl);
          this.createSaveInterval(); // Create the save interval
        })
        .catch((e) => {
          // There was an error creating the form session, just return
          console.log(e);
          return;
        });
    } else {
      // We already have a session so just create the save interval
      this.createSaveInterval();
    }
  };

  createSaveInterval = async () => {
    this.form.setState({ hasAutoSaveEnabled: true }); // Set hasAutoSaveEnabled to true
    // If we reach here, we know that we have to turn on autosaving interval
    this.autoSaveInterval = setInterval(async () => {
      if (this.form.state.pageProcessing) return; // If page processing, skip save

      // Attempt to save
      try {
        // If the tab is visible/open, allow auto-save
        if (document.visibilityState === 'visible')
          await this.handleSaveProgress(false); // Wait until save has finished
      } catch (e) {
        console.log(e); // Saving has failed, stop processing
        this.form.handleErrorState(e);
      }
    }, config.AUTOSAVE_INTERVAL * 1000);
  };

  handleSaveProgress = async (showSaveDialog) => {
    if (this.form.state.pageProcessing) return; // If already saving, return
    this.form.clearDeletedFields();
    this.form.saveUsageService();

    const { answer_data } = this.form.props.form;

    this.form.setState({ pageProcessing: true });

    try {
      let { session_id, form_id, test_id } = this.form.props.match.params;

      if (!session_id || session_id.trim() == '') {
        //no session ID, so we need to create one here
        this.form.props.match.params.session_id = uuid();
        session_id = this.form.props.match.params.session_id;
      }

      let failedUploads = {}; // Define a list of keys that failed to upload
      let data = await this.form.traverseAndFlatten(
        answer_data,
        'SAVE',
        null,
        failedUploads
      );

      const saveData = data ? { ...data } : {}; // Make a copy of the data

      if (failedUploads && Object.keys(failedUploads).length > 0) {
        Object.keys(failedUploads).map((key) => {
          if (key in saveData) delete saveData[key];
        });
      }

      let sessionURL = `${config.API_ADDRESS}/form_session?session_id=${session_id}&form_id=${form_id}`;

      if (test_id !== undefined) {
        sessionURL = `${config.API_ADDRESS}/test_form_session?session_id=${session_id}&form_id=${form_id}`;
      }

      return this.makeFormSaveRequest(sessionURL, saveData, session_id)
        .then((res) => {
          this.form.setState({
            ...(showSaveDialog && {
              saveDialog: true,
              formFailures: { failedUploads },
            }),
            pageProcessing: false,
            lastSaved: new Date().getTime(),
          });
          let formSessionURL = `/form/${this.form.props.match.params.form_id}/${session_id}`;

          if (test_id !== undefined) {
            formSessionURL = `/form/form_test/${this.form.props.match.params.form_id}/${test_id}/${session_id}`;
          }

          this.form.props.history.push(formSessionURL);
        })
        .catch((e) => {
          this.form.setState({
            saveDialog: true,
            formFailures: { failedUploads, failedSave: true },
          });
          this.form.handleErrorState(e);
          console.log(e);
        });
    } catch (e) {
      this.form.handleErrorState(e);
    }
  };

  makeFormSaveRequest = async (sessionURL, data, session_id) => {
    return axios(sessionURL, {
      method: 'post',
      data: {
        data: data,
        session_id: session_id,
        form_id: this.form.props.match.params.form_id,
        password: this.form.state.password,
        projectSessionId: this.form.state.projectSessionId,
        projectVersion: this.form.state.projectVersion,
        isSingleUse: this.form.state.isSingleUse,
        component_id: this.form.props.match.params.form_id,
        test_id: this.form.props.match.params.test_id,
        submissionId: this.form.state.submissionId,
      },
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  };

  clearInterval = () => {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;
    }
  };
}

export default AutoSaveManager;
